import { CameraRollImage } from "@components/camera-roll-image";
import { useImages } from "@features/images/use-images";
import { useUser } from "@features/user/use-user";
import {
  CameraIcon,
  ChevronLeftIcon,
  PhotoIcon,
  ScissorsIcon,
  TagIcon,
} from "@heroicons/react/16/solid";
import { Button, IconButton, Link, Text } from "@radix-ui/themes";
import { wrapGrid } from "animate-css-grid";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PromptInput } from "./prompt-input";
import { useCreateImage } from "@features/images/use-create-image";

export const KeyboardPage = () => {
  const { user } = useUser();
  const { t } = useTranslation();

  if (document.location.toString().includes("redirect-to-app")) {
    setTimeout(() => {
      window.location.href = "photographeai://setup";
    }, 500);
  }

  if (!user?.data?.id) {
    return (
      <>
        <div
          className="absolute m-auto w-full h-full bg-cover bg-center bg-no-repeat opacity-50"
          style={{
            backgroundImage: 'url("/backgrounds/hero_background.png")',
            filter: "blur(5px)",
          }}
        ></div>
        <div className="absolute z-10 m-auto top-0 bottom-0 left-0 right-0 w-max h-max bg-black px-6 py-4 rounded-md bg-opacity-75">
          <Link
            href="/keyboard?redirect-to-app"
            target="_blank"
            onClick={() => {
              if ((window as any)?.webkit?.messageHandlers?.openApp) {
                (window as any).webkit.messageHandlers.openApp.postMessage({});
              }
            }}
          >
            {t("keyboard.open_app")}
          </Link>
        </div>
      </>
    );
  }

  return <KeyboardCameraRoll />;
};

const KeyboardCameraRoll = () => {
  const { images } = useImages();
  const [mode, setMode] = useState<null | string>(null);
  const [prompt, setPrompt] = useState<string>("");
  const [model, setModel] = useState<string | undefined>();
  const { create, loading } = useCreateImage();

  useEffect(() => {
    const grid = document.querySelector(".grid");
    wrapGrid(grid as any, {
      duration: 200,
      easing: "easeOut",
    });
  }, []);

  return (
    <>
      <div className="p-2 flex flex-row items-center overflow-hidden sticky top-0 z-20 bg-slate-950 bg-opacity-80 backdrop-blur-md rounded-full">
        <AnimatePresence>
          {!!mode && (
            <motion.button
              layout
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0, width: 0 }}
              transition={{ duration: 0.2 }}
            >
              <IconButton
                onClick={() => setMode(null)}
                className="rounded-full bg-transparent mr-1"
                color="gray"
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </IconButton>
            </motion.button>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {!mode && (
            <motion.div
              layout
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100, width: 0 }}
              transition={{ duration: 0.2 }}
              className=""
            >
              <div className="flex space-x-1 mr-1">
                <motion.button onClick={() => setMode("tryon")}>
                  <IconButton
                    size="3"
                    className="rounded-full bg-slate-800"
                    color="gray"
                  >
                    <TagIcon className="w-4 h-4" />
                  </IconButton>
                </motion.button>
                <motion.button onClick={() => setMode("haircut")}>
                  <IconButton
                    size="3"
                    className="rounded-full bg-slate-800"
                    color="gray"
                  >
                    <ScissorsIcon className="w-4 h-4" />
                  </IconButton>
                </motion.button>
                <motion.button onClick={() => setMode("image")}>
                  <IconButton
                    size="3"
                    className="rounded-full bg-slate-800"
                    color="gray"
                  >
                    <PhotoIcon className="w-4 h-4" />
                  </IconButton>
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <motion.div
          layout
          transition={{ duration: 0.2 }}
          className="min-h-10 min-w-0 grow"
          onClick={() => {
            setMode("classic");
          }}
        >
          <div
            className="bg-slate-800 min-h-10 flex-grow"
            style={{ borderRadius: "20px" }}
          >
            <motion.div
              layout="position"
              className="flex flex-row items-center min-h-10 space-x-1 min-w-0 flex-grow"
            >
              {!mode && (
                <div className="p-2 flex flex-row items-center space-x-1">
                  <CameraIcon className="ml-1 w-4 h-4 text-slate-400 shrink-0" />
                  <Text className="text-slate-400 text-sm truncate">
                    Décrire ou rechercher
                  </Text>
                </div>
              )}
              {!!mode && (
                <PromptInput
                  value={prompt}
                  modelId={model}
                  onChange={(prompt, loraId) => {
                    setPrompt(prompt);
                    setModel(loraId);
                  }}
                />
              )}
            </motion.div>
          </div>
        </motion.div>
        <AnimatePresence>
          {mode === "classic" && (
            <motion.div
              layout
              initial={{ opacity: 0, x: "100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "100%", width: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Button
                disabled={loading}
                onClick={async () => {
                  try {
                    await create({
                      size: "auto",
                      mode: "classic",
                      prompt,
                      model_id: model,
                      count: 2,
                      fast: true,
                    });
                    setPrompt("");
                    setMode(null);
                  } catch (e) {
                    toast.error("Erreur lors de la création de l'image.");
                  }
                }}
                className="rounded-full ml-1"
                color="blue"
                size="3"
              >
                Créer
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className="relative select-none z-10 grid grid-cols-3 @lg:grid-cols-4 @xl:grid-cols-5 @2xl:grid-cols-6 justify-center items-center">
        {(images?.data || [])
          .filter((a) => a.status !== "failed")
          .map((image) => (
            <div key={image.id}>
              <motion.div className="overflow-hidden">
                <CameraRollImage
                  image={image}
                  onClickOverride={(el: HTMLImageElement) => {
                    if (el.complete) {
                      handleImageSend(el);
                    } else {
                      el.onload = () => handleImageSend(el);
                    }
                  }}
                />
              </motion.div>
            </div>
          ))}
      </div>
    </>
  );
};

const handleImageSend = (img: HTMLImageElement) => {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");

  if (ctx) {
    ctx.drawImage(img, 0, 0);
    canvas.toBlob(async (blob) => {
      if (blob) {
        await sendMessageWithImage(blob);
      } else {
        toast.error("Failed to create image blob.");
      }
    }, "image/jpeg");
  } else {
    toast.error("Failed to get canvas context.");
  }
};

// Example function to send a message with an image from the web
const sendMessageWithImage = async (blob: Blob) => {
  const w = window as any;
  // Make sure we're in the iMessage context
  if (
    w.webkit &&
    w.webkit.messageHandlers &&
    w.webkit.messageHandlers.sendMessageWithImage
  ) {
    // Convert imageUrl to base64
    const imageBase64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });

    // Send the message to native code
    w.webkit.messageHandlers.sendMessageWithImage.postMessage({
      text: "Photographe.ai",
      image: imageBase64, // Can be a data URL like "data:image/jpeg;base64,/9j/4AAQ..." or just the base64 string
    });
  }
};
